import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem } from '@carbon/react';
import { SettingsSkeleton } from 'src/components/Base/RequireCapability';
import PlanLock from 'src/components/Billing/PlanLock';
import StorefrontHeaderActions from 'src/components/Layout/StorefrontHeaderActions';
import StorefrontNavDropdown from 'src/components/Layout/StorefrontNavDropdown';
import StorefrontSideNav from 'src/components/Layout/StorefrontSideNav';
import { useStorefrontNav } from 'src/hooks/useStorefrontNav';

const StorefrontLayout = React.memo(() => {
  const { t } = useTranslation();
  const { ready, sections, activeItem } = useStorefrontNav();

  if (!ready) {
    return <SettingsSkeleton />;
  }

  return (
    <div className="settings-page">
      <StorefrontSideNav sections={sections} />
      <div className="settings-page__body">
        <StorefrontNavDropdown sections={sections} />
        {/* The actions sit beside the whole title block (breadcrumb + heading), top-aligned. */}
        <div className="settings-page__header settings-page__header-row">
          <div>
            <Breadcrumb noTrailingSlash aria-label="Page navigation">
              <BreadcrumbItem>
                <Link to="/storefront">{t('storefrontNav.title')}</Link>
              </BreadcrumbItem>
              {activeItem && (
                <BreadcrumbItem>
                  <Link to={activeItem.path}>{t(activeItem.labelKey)}</Link>
                </BreadcrumbItem>
              )}
            </Breadcrumb>
            <h1 className="settings-page__heading" id={'storefront-heading'}>
              {activeItem ? t(activeItem.labelKey) : t('storefrontNav.title')}
            </h1>
          </div>
          {/* View / preview are actions in the area header, not nav items (spec §9). */}
          <StorefrontHeaderActions />
        </div>
        {/* Only the editor is locked on Free: the header's Unpublish must stay reachable, since
            taking a live site down is never a paid feature (the server leaves it unchecked too). */}
        <div className="settings-page__main-content">
          <PlanLock feature="storefront">
            <Outlet />
          </PlanLock>
        </div>
      </div>
    </div>
  );
});

export default StorefrontLayout;
